import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText as FileTextIcon, X as CloseIcon } from "lucide-react";
import type {
  CreateTemplateMessageParameter,
  WhatsAppCallingStatus,
  WhatsAppSelectedPhoneNumber,
  WhatsAppTemplateDto,
} from "@multi-chat/shared";
import { isApiError } from "../../../api/client.js";
import { getWhatsAppCallingStatus } from "../../../api/inbox-api.js";
import { getConnection, listWhatsAppTemplates } from "../../../api/whatsapp-api.js";
import { useElementWidthBucket } from "../../../components/layout/use-element-width-bucket.js";
import { useMinWidth } from "../../../components/layout/use-min-width.js";
import { Button } from "../../../components/ui/button/index.js";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../../components/ui/sheet/index.js";
import { cn } from "../../../lib/utils.js";
import type { SendMessageStatus } from "../use-inbox.js";
import {
  filterTemplatesForPicker,
  isApprovedSendableTemplate,
  TEMPLATE_PICKER_SPLIT_MIN_WIDTH_PX,
  templatePickerColumns,
  templatePickerLayout,
  templatePickerStackView,
  templatePickerSurface,
  type TemplateDrawerFrame,
  type TemplatePickerColumns,
  type TemplatePickerStackView,
} from "../template-picker.js";
import {
  buildClientTemplatePresentation,
  buildTemplateSendParameters,
  discoverTemplateSendSlots,
  hasAllRequiredTemplateParameters,
  missingRequiredTemplateSlotKeys,
  templateHasVoiceCallButton,
  type TemplateSendDiscovery,
} from "../template-send.js";
import { TemplatePickerDetail, TemplatePickerPhone } from "./TemplatePickerDetail.js";
import { TemplatePickerList } from "./TemplatePickerList.js";
import { ConversationTemplateDrawer } from "./ConversationTemplateDrawer.js";
import { useWhatsAppTemplateHeaderImageUrl } from "../../whatsapp/use-whatsapp-template-header-image.js";

interface SendTemplateSheetProps {
  open: boolean;
  organizationId: number;
  conversationId: number;
  sendStatus: SendMessageStatus;
  unreachableMessage: string;
  onOpenChange: (open: boolean) => void;
  onClearSendError: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  /** Visible conversation title shown in the desktop drawer header when cheaply available. */
  contactLabel?: string | null;
  /** Conversation endpoint; names the sender in the phone preview. */
  platformEndpointId?: number;
  /** Desktop anchoring for the floating picker; without it the picker stays inside the thread. */
  drawerFrame?: TemplateDrawerFrame | null;
  onSendTemplate: (input: {
    whatsappTemplateId: number;
    parameters: CreateTemplateMessageParameter[];
    clientIdempotencyKey: string;
  }) => Promise<boolean>;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "disconnected" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: WhatsAppTemplateDto[] };

function mapTemplateSendError(
  t: (key: string) => string,
  status: Extract<SendMessageStatus, { kind: "error" }>,
): string {
  switch (status.code) {
    case "WHATSAPP_TEMPLATE_NOT_FOUND":
      return t("inbox.templateSend.errors.notFound");
    case "WHATSAPP_TEMPLATE_NOT_ELIGIBLE":
      return t("inbox.templateSend.errors.notEligible");
    case "WHATSAPP_TEMPLATE_UNSUPPORTED":
      return t("inbox.templateSend.errors.unsupported");
    case "WHATSAPP_NOT_CONNECTED":
      return t("inbox.templateSend.errors.notConnected");
    case "WHATSAPP_CALLING_NOT_ENABLED":
      return t("inbox.templateSend.errors.callingNotEnabled");
    case "WHATSAPP_TEMPLATE_HEADER_IMAGE_REQUIRED":
      return t("inbox.templateSend.errors.headerImageMissing");
    case "IDEMPOTENCY_KEY_REUSED":
      return t("inbox.templateSend.errors.idempotencyReused");
    case "CONVERSATION_NOT_FOUND":
      return t("inbox.templateSend.errors.conversationNotFound");
    case "INSUFFICIENT_MESSAGE_CREDITS":
      return t("inbox.templateSend.errors.insufficientCredits");
    default:
      return status.message;
  }
}

function payloadFingerprint(
  whatsappTemplateId: number,
  parameters: CreateTemplateMessageParameter[],
): string {
  return JSON.stringify({ whatsappTemplateId, parameters });
}

export function SendTemplateSheet({
  open,
  organizationId,
  conversationId,
  sendStatus,
  unreachableMessage,
  onOpenChange,
  onClearSendError,
  onCloseAutoFocus,
  contactLabel,
  platformEndpointId,
  drawerFrame = null,
  onSendTemplate,
}: SendTemplateSheetProps): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const isSplitViewport = useMinWidth(TEMPLATE_PICKER_SPLIT_MIN_WIDTH_PX);
  const layout = templatePickerLayout(isSplitViewport);
  const surface = templatePickerSurface(isSplitViewport);
  const [pickerBodyRef, splitColumns] = useElementWidthBucket<HTMLDivElement, TemplatePickerColumns>(
    templatePickerColumns,
  );
  const phoneColumn = layout === "split" && splitColumns === "three";
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [loadToken, setLoadToken] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [stackView, setStackView] = useState<TemplatePickerStackView>("list");
  const [query, setQuery] = useState("");
  const [languageFilter, setLanguageFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [touchedSlotKeys, setTouchedSlotKeys] = useState<ReadonlySet<string>>(new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [callingStatus, setCallingStatus] = useState<WhatsAppCallingStatus | null>(null);
  const [callingStatusLoading, setCallingStatusLoading] = useState(false);
  const [sender, setSender] = useState<WhatsAppSelectedPhoneNumber | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);
  const fingerprintRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadState({ kind: "loading" });
    setSelectedId(null);
    setStackView("list");
    setQuery("");
    setLanguageFilter(null);
    setCategoryFilter(null);
    setValues({});
    setTouchedSlotKeys(new Set());
    setSubmitAttempted(false);
    setLocalError(null);
    setCallingStatus(null);
    setSender(null);
    onClearSendError();

    void (async () => {
      try {
        const connection = await getConnection(organizationId, controller.signal);
        if (!controller.signal.aborted) {
          setSender(
            connection.phoneNumbers.find((phone) => phone.platformEndpointId === platformEndpointId) ??
              connection.workspacePhone,
          );
        }
        if (connection.state !== "CONNECTED") {
          if (!controller.signal.aborted) {
            setLoadState({ kind: "disconnected" });
          }
          return;
        }
        const listed = await listWhatsAppTemplates(organizationId, controller.signal);
        if (controller.signal.aborted) return;
        setLoadState({
          kind: "ready",
          items: listed.items.filter(isApprovedSendableTemplate),
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setLoadState({
          kind: "error",
          message: isApiError(error) ? error.message : unreachableMessage,
        });
      }
    })();

    return () => controller.abort();
  }, [open, organizationId, platformEndpointId, loadToken, unreachableMessage, onClearSendError]);

  const selectedTemplate =
    loadState.kind === "ready" && selectedId !== null
      ? (loadState.items.find((item) => item.id === selectedId) ?? null)
      : null;

  const selectedNeedsCalling =
    selectedTemplate !== null && templateHasVoiceCallButton(selectedTemplate.components);

  useEffect(() => {
    if (!open || !selectedNeedsCalling || selectedTemplate === null) {
      setCallingStatus(null);
      setCallingStatusLoading(false);
      return;
    }

    const controller = new AbortController();
    setCallingStatusLoading(true);
    setCallingStatus(null);

    void (async () => {
      try {
        const result = await getWhatsAppCallingStatus(
          organizationId,
          conversationId,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setCallingStatus(result.callingStatus);
      } catch {
        if (controller.signal.aborted) return;
        setCallingStatus("UNKNOWN");
      } finally {
        if (!controller.signal.aborted) {
          setCallingStatusLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [open, selectedNeedsCalling, selectedTemplate?.id, organizationId, conversationId]);

  const discovery: TemplateSendDiscovery | null = useMemo(() => {
    if (!selectedTemplate) return null;
    return discoverTemplateSendSlots(selectedTemplate.components);
  }, [selectedTemplate]);

  const headerImageUrl = useWhatsAppTemplateHeaderImageUrl(
    organizationId,
    selectedTemplate?.id ?? null,
    selectedTemplate != null &&
      discovery?.kind === "supported" &&
      discovery.headerFormat === "IMAGE" &&
      selectedTemplate.headerImageMissing !== true,
  );

  const valuesMap = useMemo(() => new Map(Object.entries(values)), [values]);

  const preview = useMemo(() => {
    if (!selectedTemplate || !discovery || discovery.kind !== "supported") {
      return null;
    }
    return buildClientTemplatePresentation({
      templateName: selectedTemplate.name,
      headerText: discovery.headerText,
      headerFormat: discovery.headerFormat,
      headerImageUrl,
      headerImageMissing: selectedTemplate.headerImageMissing === true,
      bodyText: discovery.bodyText,
      footerText: discovery.footerText,
      buttons: discovery.buttons,
      slots: discovery.slots,
      valuesBySlotKey: valuesMap,
    });
  }, [selectedTemplate, discovery, valuesMap, headerImageUrl]);

  const filteredItems = useMemo(() => {
    if (loadState.kind !== "ready") return [];
    return filterTemplatesForPicker(loadState.items, {
      query,
      language: languageFilter,
      category: categoryFilter,
    });
  }, [loadState, query, languageFilter, categoryFilter]);

  const missingFromValues =
    discovery?.kind === "supported"
      ? missingRequiredTemplateSlotKeys(discovery.slots, valuesMap)
      : [];
  const visibleMissingKeys = new Set(
    missingFromValues.filter((key) => submitAttempted || touchedSlotKeys.has(key)),
  );

  const callingBlocked = selectedNeedsCalling && callingStatus === "DISABLED";
  const headerImageBlocked = selectedTemplate?.headerImageMissing === true;
  const callingHint = callingBlocked
    ? t("inbox.templateSend.errors.callingNotEnabled")
    : headerImageBlocked
      ? t("inbox.templateSend.errors.headerImageMissing")
      : null;
  const sending = sendStatus.kind === "submitting";
  const sendError = sendStatus.kind === "error" ? mapTemplateSendError(t, sendStatus) : null;
  const parametersComplete =
    discovery?.kind === "supported" && hasAllRequiredTemplateParameters(discovery.slots, valuesMap);
  const sendDisabled =
    sending ||
    callingBlocked ||
    callingStatusLoading ||
    discovery?.kind !== "supported" ||
    !parametersComplete ||
    headerImageBlocked;

  const mobileView = templatePickerStackView({
    layout,
    requestedView: stackView,
    selectedId,
  });
  const showList = layout === "split" || mobileView === "list";
  const showDetail = layout === "split" || mobileView === "detail";

  function selectTemplate(templateId: number): void {
    if (sending) return;
    onClearSendError();
    setLocalError(null);
    setSubmitAttempted(false);
    setTouchedSlotKeys(new Set());
    setSelectedId(templateId);
    setValues({});
    setStackView("detail");
  }

  function updateValue(slotKey: string, value: string): void {
    onClearSendError();
    setLocalError(null);
    setValues((current) => ({ ...current, [slotKey]: value }));
  }

  function handleSlotBlur(slotKey: string): void {
    setTouchedSlotKeys((current) => {
      if (current.has(slotKey)) return current;
      const next = new Set(current);
      next.add(slotKey);
      return next;
    });
  }

  async function handleSend(): Promise<void> {
    if (!selectedTemplate || !discovery || discovery.kind !== "supported" || sending) {
      return;
    }
    if (callingBlocked || callingStatusLoading) {
      return;
    }

    setSubmitAttempted(true);
    const built = buildTemplateSendParameters(discovery.slots, valuesMap);
    if (!Array.isArray(built)) {
      setLocalError(t("inbox.templateSend.requiredField"));
      return;
    }

    const fingerprint = payloadFingerprint(selectedTemplate.id, built);
    if (fingerprintRef.current !== fingerprint) {
      idempotencyKeyRef.current = crypto.randomUUID();
      fingerprintRef.current = fingerprint;
    }
    const clientIdempotencyKey = idempotencyKeyRef.current!;

    const ok = await onSendTemplate({
      whatsappTemplateId: selectedTemplate.id,
      parameters: built,
      clientIdempotencyKey,
    });
    if (ok) {
      onOpenChange(false);
    }
  }

  const pickerReady = loadState.kind === "ready" && loadState.items.length > 0;

  const chrome = (
    <>
      <div className="flex shrink-0 items-center gap-2.5 border-b border-inbox-line-soft py-[0.5625rem] ps-3.5 pe-2">
        <span
          className="grid size-[1.875rem] shrink-0 place-items-center rounded-[0.5625rem] bg-brand-mint text-primary"
          aria-hidden="true"
        >
          <FileTextIcon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          {surface === "mobile-sheet" ? (
            <SheetTitle
              id={titleId}
              className="m-0 text-[0.875rem] font-semibold leading-tight text-inbox-strong"
            >
              {t("inbox.templateSend.title")}
            </SheetTitle>
          ) : (
            <h2
              id={titleId}
              className="m-0 text-[0.875rem] font-semibold leading-tight text-inbox-strong"
            >
              {t("inbox.templateSend.title")}
            </h2>
          )}
          {contactLabel ? (
            <p className="m-0 truncate text-[0.71875rem] text-inbox-muted">
              <bdi>{contactLabel}</bdi>
            </p>
          ) : null}
          {surface === "mobile-sheet" ? (
            <SheetDescription id={descriptionId} className="sr-only">
              {t("inbox.templateSend.description")}
            </SheetDescription>
          ) : (
            <p id={descriptionId} className="sr-only">
              {t("inbox.templateSend.description")}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-8 shrink-0 rounded-lg p-0 text-inbox-muted"
          onClick={() => onOpenChange(false)}
          disabled={sending}
          aria-label={t("inbox.templateSend.close")}
        >
          <CloseIcon size={16} aria-hidden="true" />
        </Button>
      </div>

      {loadState.kind === "loading" ? (
        <PickerStatus message={t("inbox.templateSend.loading")} />
      ) : null}

      {loadState.kind === "disconnected" ? (
        <PickerMessage
          title={t("inbox.templateSend.disconnectedTitle")}
          description={t("inbox.templateSend.disconnectedDescription")}
        />
      ) : null}

      {loadState.kind === "error" ? (
        <PickerMessage
          title={t("inbox.templateSend.loadErrorTitle")}
          description={loadState.message}
          actionLabel={t("inbox.retry")}
          onAction={() => setLoadToken((token) => token + 1)}
        />
      ) : null}

      {loadState.kind === "ready" && loadState.items.length === 0 ? (
        <PickerMessage
          title={t("inbox.templateSend.emptyTitle")}
          description={t("inbox.templateSend.emptyDescription")}
        />
      ) : null}

      {pickerReady ? (
        <div
          ref={pickerBodyRef}
          className={cn(
            "min-h-0 flex-1",
            layout === "stack"
              ? "flex flex-col"
              : phoneColumn
                ? "grid grid-cols-[minmax(12.5rem,14rem)_minmax(0,1fr)_15.75rem]"
                : "grid grid-cols-[minmax(11.5rem,12.75rem)_minmax(0,1fr)]",
          )}
        >
          {showList ? (
            <div
              className={cn(
                "flex min-h-0 min-w-0 flex-col",
                layout === "stack" ? "flex-1" : "border-e border-inbox-line-soft",
              )}
            >
              <TemplatePickerList
                items={loadState.items}
                filteredItems={filteredItems}
                selectedId={selectedId}
                query={query}
                languageFilter={languageFilter}
                categoryFilter={categoryFilter}
                sending={sending}
                onQueryChange={setQuery}
                onLanguageFilterChange={setLanguageFilter}
                onCategoryFilterChange={setCategoryFilter}
                onSelect={selectTemplate}
              />
            </div>
          ) : null}
          {showDetail ? (
            <TemplatePickerDetail
              template={selectedTemplate}
              discovery={discovery}
              preview={preview}
              values={values}
              missingSlotKeys={visibleMissingKeys}
              missingCount={missingFromValues.length}
              localError={localError}
              sendError={sendError}
              callingHint={callingHint}
              sending={sending}
              sendDisabled={sendDisabled}
              showBack={layout === "stack"}
              phonePlacement={phoneColumn ? "column" : "inline"}
              sender={sender}
              onBack={() => setStackView("list")}
              onValueChange={updateValue}
              onSlotBlur={handleSlotBlur}
              onCancel={() => onOpenChange(false)}
              onSend={() => void handleSend()}
            />
          ) : null}
          {phoneColumn ? (
            <div className="grid min-h-0 content-center overflow-hidden border-s border-inbox-line-soft bg-gradient-to-b from-[#f3f6f5] to-[#ebf1ef] p-3 [container-type:size]">
              <TemplatePickerPhone
                template={selectedTemplate}
                discovery={discovery}
                preview={preview}
                valuesBySlotKey={valuesMap}
                sender={sender}
                frameClassName="max-w-[min(13.25rem,calc((100cqh-2.75rem)*0.46))]"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  if (surface === "conversation-drawer") {
    return (
      <ConversationTemplateDrawer
        open={open}
        titleId={titleId}
        descriptionId={descriptionId}
        closeDisabled={sending}
        frame={drawerFrame}
        onOpenChange={onOpenChange}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {chrome}
      </ConversationTemplateDrawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="h-[min(92dvh,calc(100dvh-0.5rem))] max-h-[min(92dvh,calc(100dvh-0.5rem))] gap-0 overflow-hidden p-0"
        side="bottom"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {chrome}
      </SheetContent>
    </Sheet>
  );
}

function PickerStatus({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="grid flex-1 place-items-center gap-3 px-6 py-10" role="status">
      <div
        className="size-7 animate-spin rounded-full border-2 border-line border-t-accent"
        aria-hidden="true"
      />
      <p className="m-0 text-[0.8125rem] text-ink-muted">{message}</p>
    </div>
  );
}

function PickerMessage({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}): React.JSX.Element {
  return (
    <div className="grid flex-1 place-items-center px-6 py-10">
      <div className="max-w-sm text-center">
        <p className="m-0 text-[0.9375rem] font-semibold text-ink">{title}</p>
        <p className="m-0 mt-1.5 text-[0.8125rem] leading-snug text-ink-muted">{description}</p>
        {onAction && actionLabel ? (
          <Button type="button" variant="outline" size="sm" className="mt-4 rounded-full" onClick={onAction}>
            {actionLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
